import { useState } from "react";
import Toast from "@/components/base/Toast";
import { useToast } from "@/hooks/useToast";
import { hotKeywords, reminderChannels, reminderTimings } from "@/mocks/home";

export default function ReminderPanel() {
  const { message, show } = useToast();
  const [channels, setChannels] = useState<string[]>(["c-site", "c-browser"]);
  const [timing, setTiming] = useState("time-key");

  const toggleChannel = (id: string) => {
    setChannels((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const handleSave = () => {
    if (channels.length === 0) {
      show("请至少选择一个提醒渠道");
      return;
    }
    show("提醒设置已保存（演示效果）");
  };

  return (
    <section id="reminder" className="border-b border-background-200">
      <div className="mx-auto w-full max-w-6xl px-4 py-14 md:px-8 md:py-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <span className="font-label text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
              Reminder Setup
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              更新提醒设置
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              挑一个推送渠道，再选一个提醒时机，重点场次开更就能第一时间收到推送。
              不想被打扰的老粉，直接选「只提醒重点场次」，一天两条，干净利落。
            </p>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100/70 p-4">
              <div className="flex items-center gap-2 text-xs font-medium text-foreground-700">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-lightbulb-line text-accent-600"></i>
                </span>
                蹲更新小技巧
              </div>
              <p className="mt-2 text-xs leading-6 text-foreground-500">
                免费订阅、随时可关。把提醒时机设成「更新前 10 分钟」，还能顺便给自己留出泡茶的时间。
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {hotKeywords.map((kw) => (
                <span
                  key={kw}
                  className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900"
                >
                  {kw}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="text-sm font-bold text-foreground-900">1 · 选择推送渠道</h3>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {reminderChannels.map((ch) => {
                  const active = channels.includes(ch.id);
                  return (
                    <button
                      key={ch.id}
                      type="button"
                      onClick={() => toggleChannel(ch.id)}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 text-left transition-colors ${
                        active
                          ? "border-primary-400 bg-primary-50"
                          : "border-background-200 bg-background-50 hover:border-background-300"
                      }`}
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                          active ? "bg-primary-500 text-background-50 dark:text-foreground-950" : "bg-background-100 text-foreground-600"
                        }`}
                      >
                        <i className={ch.icon}></i>
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground-900">{ch.name}</span>
                          {active && (
                            <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-600">
                              <i className="ri-check-line"></i>
                            </span>
                          )}
                        </span>
                        <span className="mt-1 block text-xs leading-5 text-foreground-500">
                          {ch.desc}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <h3 className="mt-7 text-sm font-bold text-foreground-900">2 · 选择提醒时机</h3>
              <div className="mt-4 space-y-3">
                {reminderTimings.map((t) => {
                  const active = timing === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTiming(t.id)}
                      className={`flex w-full cursor-pointer items-center gap-3 rounded-lg border px-4 py-3.5 text-left transition-colors ${
                        active
                          ? "border-secondary-400 bg-secondary-50"
                          : "border-background-200 hover:border-background-300"
                      }`}
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                          active ? "border-secondary-500" : "border-background-300"
                        }`}
                      >
                        {active && <span className="h-2.5 w-2.5 rounded-full bg-secondary-500" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-900">
                          {t.label}
                        </span>
                        <span className="mt-0.5 block text-xs text-foreground-500">{t.hint}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-7 flex flex-col items-start justify-between gap-3 border-t border-background-200 pt-5 sm:flex-row sm:items-center">
                <p className="text-xs text-foreground-500">
                  已选 <span className="font-medium text-foreground-800">{channels.length}</span> 个渠道
                </p>
                <button
                  type="button"
                  onClick={handleSave}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-notification-3-line"></i>
                  </span>
                  保存提醒设置
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toast message={message} />
    </section>
  );
}